import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import '../styles/components/qr-scanner.css';

interface QRBarcodeScannerProps {
  onScanSuccess: (_decodedText: string) => boolean | void; // Return false to indicate invalid QR and stop scanning
  onScanError?: (_error: string) => void;
  onClose: () => void;
}

/* ── Barcode SVG paths (from Figma reference) ── */
const barcodePaths = [
  "M34.2773 0H37.8974V36.2938H34.2773V0Z",
  "M53.8457 0H57.4657V36.2938H53.8457V0Z",
  "M60.2811 4.98246e-05H63.9311V36.2938H60.2811V4.98246e-05Z",
  "M9.62493 4.98246e-05H13.1871V36.249H9.62493V4.98246e-05Z",
  "M18.5332 0.000976562H26.1474V36.2945L18.5332 36.2492V0.000976562Z",
  "M43.3867 0H50.9724V36.2938H43.3867V0Z",
  "M29.8828 0H31.6349V36.2938H29.8828V0Z",
  "M0 0H7.69958V36.2489H0V0Z",
];

/* ── QR icon SVG paths (from Figma reference) ── */
const qrIconPaths = [
  "M3.125 3.125H9.375V0H0V9.375H3.125V3.125Z",
  "M30.625 0V3.125H36.875V9.375H40V0H30.625Z",
  "M3.125 30.625H0V40H9.375V36.875H3.125V30.625Z",
  "M36.875 36.875H30.625V40H40V30.625H36.875V36.875Z",
  "M5.9375 18.4375H18.4375V5.9375H5.9375V18.4375ZM9.0625 9.0625H15.3125V15.3125H9.0625V9.0625Z",
  "M34.0625 5.9375H21.5625V18.4375H34.0625V5.9375ZM30.9375 15.3125H24.6875V9.0625H30.9375V15.3125Z",
  "M5.9375 34.0625H18.4375V21.5625H5.9375V34.0625ZM9.0625 24.6875H15.3125V30.9375H9.0625V24.6875Z",
  "M24.6875 24.6875V21.5625H21.5625V27.8125H24.6875V30.9375H27.8125V24.6875H24.6875Z",
  "M27.8125 30.9375H34.0625V34.0625H27.8125V30.9375Z",
  "M30.9375 27.8125H34.0625V21.5625H27.8125V24.6875H30.9375V27.8125Z",
  "M21.5625 30.9375H24.6875V34.0625H21.5625V30.9375Z",
];

const QRBarcodeScanner: React.FC<QRBarcodeScannerProps> = ({
  onScanSuccess,
  onScanError,
  onClose,
}) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [progress, setProgress] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const scannerId = 'qr-reader';
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasScannedRef = useRef<boolean>(false);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* ── Scan line animation (JavaScript-driven for smooth 60fps) ── */
  const [scanY, setScanY] = useState(20);
  useEffect(() => {
    let y = 20, dir = 1;
    const id = setInterval(() => {
      y += dir * 0.8;
      if (y >= 78) { y = 78; dir = -1; }
      if (y <= 20) { y = 20; dir = 1; }
      setScanY(y);
    }, 16);
    return () => clearInterval(id);
  }, []);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (err) {
        // Ignore errors when stopping (might already be stopped)
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const startScanner = async () => {
      try {
        // Check permissions first
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
        } catch (permError: any) {
          if (permError.name === 'NotAllowedError' || permError.name === 'PermissionDeniedError') {
            setShowPermissionModal(true);
            return;
          }
          throw permError;
        }

        const devices = await Html5Qrcode.getCameras();
        if (devices && devices.length > 0) {
          let backCamera = devices.find(
            d =>
              d.label.toLowerCase().includes('back') ||
              d.label.toLowerCase().includes('rear') ||
              d.label.toLowerCase().includes('environment')
          );
          if (!backCamera) {
            backCamera = devices[devices.length - 1];
          }

          const cameraId = backCamera.id;

          if (!isMounted) return;
          setIsScanning(true);
          hasScannedRef.current = false;
          const scanner = new Html5Qrcode(scannerId, { verbose: false });
          scannerRef.current = scanner;

          // Progress bar: auto-increments to simulate activity
          progressIntervalRef.current = setInterval(() => {
            if (!isMounted) {
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
              return;
            }
            setProgress(prev => {
              if (prev >= 90) {
                if (progressIntervalRef.current) {
                  clearInterval(progressIntervalRef.current);
                  progressIntervalRef.current = null;
                }
                return 90;
              }
              return prev + 0.4;
            });
          }, 30);

          // 30 second timeout for no QR code detected
          scanTimeoutRef.current = setTimeout(() => {
            if (!hasScannedRef.current && isMounted) {
              if (onScanError) {
                onScanError('No QR code detected. Please ensure you are scanning a valid QR code.');
              }
              stopScanner().then(() => {
                if (isMounted) onClose();
              }).catch(() => {
                if (isMounted) onClose();
              });
            }
          }, 30000);

          await scanner.start(
            cameraId,
            {
              fps: 15,
              qrbox: { width: 300, height: 200 },
            },
            decodedText => {
              console.log('✅ Scanned successfully:', decodedText);

              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }

              if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
                scanTimeoutRef.current = null;
              }

              if (!isMounted) return;
              setProgress(100);
              hasScannedRef.current = true;

              const result = onScanSuccess(decodedText);

              if (result === false) {
                stopScanner().then(() => {
                  if (isMounted) onClose();
                }).catch(() => {
                  if (isMounted) onClose();
                });
                return;
              }

              setTimeout(() => {
                stopScanner().catch(() => {});
              }, 500);
            },
            _errorMessage => {
              // Expected during scanning when no code is detected
            }
          );
        } else {
          if (onScanError) {
            onScanError('No camera found. Please ensure your device has a camera.');
          }
          onClose();
        }
      } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to start camera';
        if (errorMessage.includes('The play() request was interrupted')) {
          return;
        }
        if (isMounted) {
          if (onScanError) {
            onScanError(`Camera error: ${errorMessage}. Please try again.`);
          }
          onClose();
        }
      }
    };

    startScanner();

    return () => {
      isMounted = false;

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }

      stopScanner().catch(() => {});
    };
  }, [stopScanner, onScanSuccess, onScanError, onClose]);

  const handleClose = async () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }

    try {
      await stopScanner();
    } catch (error) {
      console.warn('Error stopping scanner during close:', error);
    }
    onClose();
  };

  const handleEnableCamera = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ action: "OPEN_APP_SETTINGS" })
      );
    }
    setShowPermissionModal(false);
    onClose();
  };

  /* ── Corner bracket styles for the scan window ── */
  const cornerStyles: React.CSSProperties[] = [
    { top: 0, left: 0, borderTop: '3px solid #217C70', borderLeft: '3px solid #217C70', borderRadius: '6px 0 0 0' },
    { top: 0, right: 0, borderTop: '3px solid #217C70', borderRight: '3px solid #217C70', borderRadius: '0 6px 0 0' },
    { bottom: 0, left: 0, borderBottom: '3px solid #217C70', borderLeft: '3px solid #217C70', borderRadius: '0 0 0 6px' },
    { bottom: 0, right: 0, borderBottom: '3px solid #217C70', borderRight: '3px solid #217C70', borderRadius: '0 0 6px 0' },
  ];

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-container">
        {/* Header */}
        <div className="qr-scanner-header">
          <button onClick={handleClose} className="qr-scanner-back-btn">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
          <h3 className="qr-scanner-header-title">Add New Device</h3>
        </div>

        {/* Body */}
        <div className="qr-scanner-body">
          {/* QR icon */}
          <div className="qr-scanner-icon-wrap">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              {qrIconPaths.map((d, i) => (
                <path key={i} d={d} fill="#487A9F" />
              ))}
            </svg>
          </div>

          <h2 className="qr-scanner-title">Scanning QR Code</h2>
          <p className="qr-scanner-subtitle">
            Upload/Scan the QR code from the back of the device
          </p>

          {/* Scanner box — dark area with camera feed + overlays */}
          <div className="qr-scanner-dark-box">
            {/* Noise texture */}
            <div className="qr-scanner-noise" />
            {/* Vignette */}
            <div className="qr-scanner-vignette" />

            {/* Camera feed (html5-qrcode renders here) */}
            <div id={scannerId} className="qr-scanner-reader" />

            {/* Scan window frame */}
            <div className="qr-scanner-frame">
              {/* Barcode placeholder (shown until camera starts) */}
              <div className="qr-scanner-barcode">
                <svg width="96" height="56" viewBox="0 0 63.9312 36.2945" fill="none">
                  {barcodePaths.map((d, i) => (
                    <path key={i} d={d} fill="white" />
                  ))}
                </svg>
              </div>

              {/* Illuminated strip above scan line */}
              <div
                className="qr-scanner-illumination"
                style={{ top: `calc(${scanY}% - 14px)` }}
              />

              {/* Corner brackets */}
              {cornerStyles.map((s, i) => (
                <div key={i} className="qr-scanner-corner" style={s} />
              ))}

              {/* Scan line */}
              <div className="qr-scanner-line" style={{ top: `${scanY}%` }} />

              {/* Glow above scan line */}
              <div
                className="qr-scanner-line-glow"
                style={{ top: `${scanY}%` }}
              />
            </div>

            {/* Pulsing ring */}
            <div className="qr-scanner-pulse-ring" />
          </div>

          {/* Progress section */}
          {isScanning && (
            <div className="qr-scanner-progress">
              <span className="qr-scanner-progress-label">Fetching Device Info</span>
              <div className="qr-scanner-progress-track">
                <div
                  className="qr-scanner-progress-fill"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Bottom bar with cancel button */}
        <div className="qr-scanner-bottom-bar">
          <button onClick={handleClose} className="qr-scanner-cancel-btn">
            Cancel
          </button>
        </div>
      </div>

      {/* Camera Permission Denied Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-md w-full relative overflow-hidden">
            <button
              onClick={() => setShowPermissionModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <div className="p-8 flex flex-col items-center text-center">
              <div className="mb-6">
                <img
                  src={`${process.env.PUBLIC_URL}/SVGIcons/camera.svg`}
                  alt="Camera Blocked"
                  style={{ width: '96px', height: '96px' }}
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-3">Camera Access Required</h2>
              <p className="text-gray-600 text-sm mb-2 max-w-sm leading-relaxed">
                To scan devices, please enable camera access:
              </p>
              <ul className="text-left text-gray-600 text-sm mb-6 space-y-1 max-w-sm">
                <li className="flex items-start">
                  <span className="mr-2">&bull;</span>
                  <span><strong>Browser:</strong> Click the camera icon in the address bar and allow access</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">&bull;</span>
                  <span><strong>Mobile:</strong> Go to Settings &rarr; Browser &rarr; Camera permissions</span>
                </li>
              </ul>
              <p className="text-gray-500 text-xs mb-6 italic">
                After enabling camera, return here to continue adding your device.
              </p>

              <button
                onClick={handleEnableCamera}
                className="w-full max-w-xs text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                style={{ background: 'linear-gradient(to right, #0F796B, #6FDC95)' }}
              >
                Go to App Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRBarcodeScanner;
